

import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { WeeklyLineupData, RosterCardInfo } from '@/services/events/main-event';
import { CountdownTimer } from './widgets';
import {
  STARTER_SLOT_ORDER,
  eligibleSwapTargets,
  applySwap,
  lineupDiffers,
} from './lineup-swap';

/**
 * DRAFT_PPR pre-lock lineup editor: the chosen 8 in their slots above a 2-card
 * bench tray, with TAP-TO-SWAP (tap a bench card → eligible starter slots
 * highlight by position incl. FLEX → tap a starter to complete the swap) and a
 * Save → PUT roster/lineup. No drag-and-drop in v1. Visual idiom mirrors
 * RosterWeekPanel (slot chip + line-art avatar rows).
 */

/**
 * Structured save failure. The parent's onSave should throw one of these
 * (built from the API error body) so the editor renders code-specific copy
 * (LINEUP_SHAPE_INVALID missing slots, LINEUP_WEEK_LOCKED, …).
 */
export class LineupSaveError extends Error {
  readonly body: { error?: string; code?: string } & Record<string, unknown>;

  constructor(body: { error?: string; code?: string } & Record<string, unknown>) {
    super(body.error || body.code || 'Failed to save lineup');
    this.name = 'LineupSaveError';
    this.body = body;
  }
}

const SLOT_NAMES: Record<string, string> = {
  QB: 'a Quarterback',
  RB: 'a Running Back',
  WR: 'a Wide Receiver',
  TE: 'a Tight End',
  K: 'a Kicker',
  FLEX: 'a Flex (RB/WR/TE)',
};

/** Map a lineup save failure to user-facing copy (structured codes first). */
function describeSaveError(err: unknown): string {
  if (err instanceof LineupSaveError) {
    const b = err.body;
    switch (b.code) {
      case 'LINEUP_WEEK_LOCKED':
        return 'This week is locked — the first game has kicked off.';
      case 'LINEUP_SHAPE_INVALID': {
        const missing = Array.isArray(b.missing) ? (b.missing as string[]) : [];
        const named = missing.map((m) => SLOT_NAMES[m] ?? m).join(', ');
        return missing.length > 0
          ? `Your starters can't fill: ${named}.`
          : 'Your starters can’t fill the required lineup slots.';
      }
      case 'LINEUP_SIZE_INVALID':
        return 'A lineup needs exactly 8 starters.';
      case 'LINEUP_CARD_NOT_ROSTERED':
        return 'A starter is not on your roster — refresh and try again.';
      case 'NOT_ENTERED':
        return 'You have not entered this league.';
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : 'Failed to save lineup';
}

// Same slot chip palette as RosterWeekPanel (position-level server slots).
const SLOT_STYLE: Record<string, { color: string; bg: string }> = {
  QB: { color: 'text-[#DC2626]', bg: 'bg-[#DC2626]/15' },
  RB: { color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  WR: { color: 'text-[#3B82F6]', bg: 'bg-[#3B82F6]/15' },
  TE: { color: 'text-orange-400', bg: 'bg-orange-500/15' },
  FLEX: { color: 'text-gold', bg: 'bg-gold/15' },
  K: { color: 'text-indigo-400', bg: 'bg-indigo-500/15' },
  BN: { color: 'text-warm-gray', bg: 'bg-zinc-500/15' },
};

function PlayerCells({ player, slotLabel }: { player: RosterCardInfo; slotLabel: string }) {
  const style = SLOT_STYLE[slotLabel] ?? SLOT_STYLE.BN;
  return (
    <>
      {/* Slot chip */}
      <span
        className={cn(
          'w-11 shrink-0 text-center px-1.5 py-1 rounded text-[10px] font-bold tracking-wider',
          style.bg,
          style.color,
        )}
      >
        {slotLabel}
      </span>

      {/* Line-art avatar */}
      <span className="w-9 h-9 rounded-full bg-parchment border border-parchment-border overflow-hidden flex items-center justify-center shrink-0">
        {player.imageUrl ? (
          <img src={player.imageUrl} alt="" className="w-full h-full object-cover object-top" />
        ) : (
          <span className="text-xs font-serif font-bold text-surface/50">
            {player.playerName.split(' ').map((n) => n[0]).join('')}
          </span>
        )}
      </span>

      {/* Name + meta */}
      <span className="flex-1 min-w-0 text-left">
        <span className="block text-sm font-medium text-cream truncate">{player.playerName}</span>
        <span className="block text-[11px] text-tertiary truncate">
          {player.position}
          {player.team ? ` · ${player.team}` : ''}
        </span>
      </span>
    </>
  );
}

interface LineupEditorProps {
  /** The chosen-or-default lineup from GET roster/lineup (all 10 cards). */
  lineup: WeeklyLineupData;
  week: number;
  /** PUTs the lineup; throws LineupSaveError on a structured failure. */
  onSave: (starterCardIds: string[], slots: Record<string, string>) => Promise<void>;
}

export function LineupEditor({ lineup, week, onSave }: LineupEditorProps) {
  const playerByCard = useMemo(
    () => new Map(lineup.players.map((p) => [p.cardId, p])),
    [lineup.players],
  );

  // Server truth: starter cardId → slot (bench cards are absent from the map).
  const savedSlotByCard = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of lineup.players) {
      if (p.slot !== 'BENCH') m.set(p.cardId, p.slot);
    }
    return m;
  }, [lineup.players]);

  const [slotByCard, setSlotByCard] = useState<Map<string, string>>(savedSlotByCard);
  const [selectedBench, setSelectedBench] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const starters = useMemo(() => {
    const rows = [...slotByCard.entries()]
      .map(([cardId, slot]) => ({ player: playerByCard.get(cardId), slot }))
      .filter((r): r is { player: RosterCardInfo; slot: string } => !!r.player);
    return rows.sort((a, b) => {
      const d =
        STARTER_SLOT_ORDER.indexOf(a.slot as (typeof STARTER_SLOT_ORDER)[number]) -
        STARTER_SLOT_ORDER.indexOf(b.slot as (typeof STARTER_SLOT_ORDER)[number]);
      return d !== 0 ? d : a.player.playerName.localeCompare(b.player.playerName);
    });
  }, [slotByCard, playerByCard]);

  const bench = useMemo(
    () => lineup.players.filter((p) => !slotByCard.has(p.cardId)),
    [lineup.players, slotByCard],
  );

  // Highlighted swap targets while a bench card is selected.
  const eligibleTargets = useMemo(() => {
    if (!selectedBench) return new Set<string>();
    const benchCard = playerByCard.get(selectedBench);
    if (!benchCard) return new Set<string>();
    return eligibleSwapTargets(
      benchCard.position,
      starters.map((s) => ({ cardId: s.player.cardId, slot: s.slot })),
    );
  }, [selectedBench, playerByCard, starters]);

  const dirty = lineupDiffers(slotByCard, savedSlotByCard);

  const handleBenchTap = (cardId: string) => {
    setError(null);
    setSelectedBench((prev) => (prev === cardId ? null : cardId));
  };

  const handleStarterTap = (starterCardId: string) => {
    if (!selectedBench || !eligibleTargets.has(starterCardId)) return;
    const next = applySwap(slotByCard, selectedBench, starterCardId);
    if (next) {
      setSlotByCard(next);
      setSelectedBench(null);
      setError(null);
    }
  };

  const handleReset = () => {
    setSlotByCard(savedSlotByCard);
    setSelectedBench(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave([...slotByCard.keys()], Object.fromEntries(slotByCard));
    } catch (err) {
      setError(describeSaveError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-2 overflow-hidden max-w-md">
      {/* Header — week label + lock countdown */}
      <div className="px-4 py-3 border-b border-surface-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] text-tertiary font-bold">
            Set Your Lineup — Week {week}
          </p>
          <p className="text-[11px] text-warm-gray mt-0.5">Locks at first kickoff</p>
        </div>
        {lineup.lockTime && <CountdownTimer lockTime={lineup.lockTime} />}
      </div>

      {/* Default-lineup nudge */}
      {lineup.isDefault && !dirty && (
        <div className="px-4 py-2 bg-gold/10 border-b border-gold/20">
          <p className="text-xs text-gold">Default lineup — edit to set Week {week}.</p>
        </div>
      )}

      {/* Chosen 8 — highlighted rows are legal swap targets for the selected bench card */}
      <div className="divide-y divide-surface-3/60">
        {starters.map(({ player, slot }) => {
          const isTarget = eligibleTargets.has(player.cardId);
          const dimmed = !!selectedBench && !isTarget;
          return (
            <button
              key={player.cardId}
              type="button"
              onClick={() => handleStarterTap(player.cardId)}
              disabled={!isTarget}
              aria-label={
                isTarget ? `Swap into the ${slot} slot for ${player.playerName}` : undefined
              }
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 transition-colors',
                isTarget && 'bg-gold/5 ring-1 ring-inset ring-gold/50 cursor-pointer hover:bg-gold/10',
                dimmed && 'opacity-40',
              )}
            >
              <PlayerCells player={player} slotLabel={slot} />
            </button>
          );
        })}
      </div>

      {/* Bench tray — tap to arm a swap */}
      <div className="border-t border-surface-3">
        <p className="px-4 pt-2 text-[10px] uppercase tracking-[0.2em] text-tertiary/70 font-bold">
          Bench — tap a card to swap it in
        </p>
        <div className="divide-y divide-surface-3/40 pb-1">
          {bench.map((player) => {
            const selected = selectedBench === player.cardId;
            return (
              <button
                key={player.cardId}
                type="button"
                onClick={() => handleBenchTap(player.cardId)}
                aria-pressed={selected}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 transition-colors',
                  selected
                    ? 'bg-gold/10 ring-1 ring-inset ring-gold'
                    : 'opacity-60 hover:opacity-100',
                )}
              >
                <PlayerCells player={player} slotLabel="BN" />
                <span
                  className={cn(
                    'text-[10px] font-bold uppercase tracking-wider shrink-0',
                    selected ? 'text-gold' : 'text-tertiary',
                  )}
                >
                  {selected ? 'Pick a slot' : 'Swap'}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer — error copy / swap hint + Reset / Save */}
      <div className="px-4 py-3 border-t border-surface-3 flex items-center gap-2">
        {error ? (
          <p className="text-xs text-oxblood flex-1 min-w-0">{error}</p>
        ) : selectedBench ? (
          <p className="text-xs text-warm-gray flex-1 min-w-0">
            Tap a highlighted starter to complete the swap.
          </p>
        ) : (
          <span className="flex-1" />
        )}
        {dirty && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="text-warm-gray hover:text-cream shrink-0"
          >
            Reset
          </Button>
        )}
        <Button
          variant="coins"
          size="sm"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="shrink-0"
        >
          {saving ? 'Saving…' : 'Save Lineup'}
        </Button>
      </div>
    </div>
  );
}
